import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { isAuthenticatedForRole } from "@/utils/auth";
import { hasAnyPermission, STAFF_ACCOUNT_TYPE, STAFF_PERMISSION, getStaffIdentity } from "@/utils/staffAuth";

import PublicLayout from "@/components/layout/PublicLayout";
import StudentLayout from "@/components/layout/StudentLayout";
import AdminLayout from "@/components/layout/AdminLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Course from "@/pages/Course";
import CalendarPage from "@/pages/CalendarPage";
import Locations from "@/pages/Locations";
import FAQPage from "@/pages/FAQPage";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Checkout from "@/pages/Checkout";

import StudentDashboard from "@/pages/student/Dashboard";
import StudentProfile from "@/pages/student/Profile";
import OnlineEnrolmentNew from "@/pages/student/OnlineEnrolmentNew";
import MyCourses from "@/pages/student/MyCourses";
import StudentEmailLog from "@/pages/student/StudentEmailLog";
import StudentCourseProgress from "@/pages/student/StudentCourseProgress";
import StudentBookings from "@/pages/student/Bookings";
import StudentEnrolments from "@/pages/student/Enrolments";
import StudentPayments from "@/pages/student/Payments";
import StudentCertificates from "@/pages/student/Certificates";
import StudentDocuments from "@/pages/student/Documents";
import IdentityDocuments from "@/pages/student/IdentityDocuments";
import PreClassPreparation from "@/pages/student/PreClassPreparation";
import ReadingResources from "@/pages/student/ReadingResources";
import StudentNotifications from "@/pages/student/Notifications";
import Support from "@/pages/student/Support";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminClasses from "@/pages/admin/AdminClasses";
import AdminTrainers from "@/pages/admin/AdminTrainers";
import AdminEnrolments from "@/pages/admin/AdminEnrolments";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminCourseProgress from "@/pages/admin/AdminCourseProgress";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import AdminReports from "@/pages/admin/AdminReports";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminSupport from "@/pages/admin/AdminSupport";
import StaffUsers from "@/pages/admin/StaffUsers";
import AdminDatabaseBackup from "@/pages/admin/AdminDatabaseBackup";
import EnrolmentFormStage from "@/components/course-progress/stages/EnrolmentFormStage";
import EnrollmentDocumentsStage from "@/components/course-progress/stages/EnrollmentDocumentsStage";
import USINumberStage from "@/components/course-progress/stages/USINumberStage";
import PreClassPreparationStage from "@/components/course-progress/stages/PreClassPreparationStage";
import ReadingResourcesStage from "@/components/course-progress/stages/ReadingResourcesStage";
import PreTrainingReviewStage from "@/components/course-progress/stages/PreTrainingReviewStage";
import PreClassInstructionStage from "@/components/course-progress/stages/PreClassInstructionStage";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/course" element={<Course />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/checkout/:classId?" element={<Checkout />} />
            <Route path="/admin/login" element={<AdminLogin />} />
          </Route>

          {/* Student portal */}
          <Route path="/student" element={<RequireAuth role="Student" loginPath="/login"><StudentLayout /></RequireAuth>}>
            <Route index element={<Navigate to="/student/dashboard" replace />} />
            <Route path="dashboard" element={<StudentDashboard />} />
            <Route path="profile" element={<StudentProfile />} />
            <Route path="enrol-new" element={<OnlineEnrolmentNew />} />
            <Route path="my-courses" element={<MyCourses />} />
            <Route path="email-log" element={<StudentEmailLog />} />
            <Route path="my-courses/:studentBookingId" element={<CourseProgressRedirect />} />
            <Route path="caurse-progress/:studentBookingId" element={<StudentCourseProgress />} />
            <Route path="caurse-progress/:studentBookingId/enrolment" element={<EnrolmentFormStage mode="student" />} />
            <Route path="caurse-progress/:studentBookingId/usi-number" element={<USINumberStage mode="student" />} />
            <Route path="caurse-progress/:studentBookingId/documents" element={<EnrollmentDocumentsStage mode="student" />} />
            <Route path="caurse-progress/:studentBookingId/lln-test" element={<PreClassPreparationStage mode="student" stage="lln" />} />
            <Route path="caurse-progress/:studentBookingId/pre-class-preparation" element={<PreClassPreparationStage mode="student" />} />
            <Route path="caurse-progress/:studentBookingId/pre-training-review" element={<PreTrainingReviewStage mode="student" />} />
            <Route path="caurse-progress/:studentBookingId/pre-class-instruction" element={<PreClassInstructionStage mode="student" />} />
            <Route path="caurse-progress/:studentBookingId/reading-resources" element={<ReadingResourcesStage mode="student" />} />
            <Route path="bookings" element={<StudentBookings />} />
            <Route path="enrolments" element={<StudentEnrolments />} />
            <Route path="payments" element={<StudentPayments />} />
            <Route path="certificates" element={<StudentCertificates />} />
            <Route path="documents" element={<StudentDocuments />} />
            <Route path="usi-number" element={<USINumberStage mode="student" />} />
            <Route path="enrollment-documents" element={<IdentityDocuments />} />
            <Route path="pre-class-preparation" element={<PreClassPreparation />} />
            <Route path="reading-resources" element={<ReadingResources />} />
            <Route path="notifications" element={<StudentNotifications />} />
            <Route path="support" element={<Support />} />
          </Route>

          {/* Admin portal */}
          <Route path="/admin" element={<RequireAuth role="Admin" loginPath="/admin/login"><AdminLayout /></RequireAuth>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="email-log" element={<AdminEmailLog />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="classes" element={<AdminClasses />} />
            <Route path="trainers" element={<AdminTrainers />} />
            <Route path="enrolments" element={<RequireStaffPermission permissions={[STAFF_PERMISSION.ENROLMENT_VIEW]} allowTrainer><AdminEnrolments /></RequireStaffPermission>} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="caurse-progress/:studentBookingId" element={<AdminCourseProgress />} />
            <Route path="caurse-progress/:studentBookingId/enrolment" element={<EnrolmentFormStage mode="admin" />} />
            <Route path="caurse-progress/:studentBookingId/usi-number" element={<USINumberStage mode="admin" />} />
            <Route path="caurse-progress/:studentBookingId/documents" element={<EnrollmentDocumentsStage mode="admin" />} />
            <Route path="caurse-progress/:studentBookingId/lln-test" element={<PreClassPreparationStage mode="admin" stage="lln" />} />
            <Route path="caurse-progress/:studentBookingId/pre-class-preparation" element={<PreClassPreparationStage mode="admin" />} />
            <Route path="caurse-progress/:studentBookingId/pre-training-review" element={<PreTrainingReviewStage mode="admin" />} />
            <Route path="caurse-progress/:studentBookingId/pre-class-instruction" element={<PreClassInstructionStage mode="admin" />} />
            <Route path="caurse-progress/:studentBookingId/reading-resources" element={<ReadingResourcesStage mode="admin" />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="certificates" element={<RequireNonTrainer><AdminCertificates /></RequireNonTrainer>} />
            <Route path="attendance" element={<RequireStaffPermission permissions={[STAFF_PERMISSION.ATTENDANCE_VIEW]}><AdminAttendance /></RequireStaffPermission>} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="staff-users" element={<RequireSuperAdmin><StaffUsers /></RequireSuperAdmin>} />
            <Route path="database-backup" element={<RequireSuperAdmin><AdminDatabaseBackup /></RequireSuperAdmin>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function CourseProgressRedirect() {
  const { studentBookingId } = useParams();
  return <Navigate to={`/student/caurse-progress/${studentBookingId}`} replace />;
}

function RequireAuth({ role, loginPath, children }) {
  const location = useLocation();

  if (!isAuthenticatedForRole(role)) {
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  return children;
}

function RequireStaffPermission({ permissions, allowTrainer = false, children }) {
  const isTrainer = getStaffIdentity().accountTypeId === STAFF_ACCOUNT_TYPE.TRAINER;
  return (hasAnyPermission(...permissions) || (allowTrainer && isTrainer)) ? children : <Navigate to="/admin/dashboard" replace />;
}

function RequireNonTrainer({ children }) {
  return getStaffIdentity().accountTypeId !== STAFF_ACCOUNT_TYPE.TRAINER ? children : <Navigate to="/admin/dashboard" replace />;
}

function RequireSuperAdmin({ children }) {
  return getStaffIdentity().accountTypeId === STAFF_ACCOUNT_TYPE.SUPER_ADMIN ? children : <Navigate to="/admin/dashboard" replace />;
}

export default App;
